import React, { useState, useEffect } from "react";
import { Container, Table, Spinner, Modal } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { toast } from "react-toastify";

// Images
import homeImage from "../../assets/images/home.svg";
import viewAction from "../../assets/images/viewaction.svg";
import deleteAction from "../../assets/images/deleteaction.svg";
import addicon from "../../assets/images/addicon.svg";
import bin from "../../assets/images/bin.png";
import listicon from "../../assets/images/list.svg";
import cardicon from "../../assets/images/cardicon.svg";

// Components
import Loader, { LoaderType } from "../common-component/Loader";
import Nodata from "../common-component/Nodata";
import {
    StatsSkeleton,
    DatabaseCardsGridSkeleton
} from "./DatabaseSkeleton";

// Redux
import {
    DatabaseGetList,
    DatabaseDelete,
    DatabaseStart,
    DatabaseStop,
    setDatabaseGetListData,
} from "../../services/ReduxTookit/Slice/DatabaseRedux";

// Database Icon SVG Component
const DatabaseIcon = ({ type, size = 24 }) => {
    const colors = {
        postgresql: "#336791",
        mysql: "#00618a",
        mongodb: "#47a248",
        redis: "#dc382d",
        mssql: "#cc292b",
        sqlserver: "#cc292b",
        mariadb: "#c07158",
        default: "#2b8dc8",
    };
    const color = colors[type] || colors.default;

    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
            <ellipse cx="12" cy="5" rx="9" ry="3" fill={color} />
            <path
                d="M3 5v7c0 1.66 4.03 3 9 3s9-1.34 9-3V5"
                stroke={color}
                strokeWidth="1.5"
                fill="none"
            />
            <path
                d="M3 12v7c0 1.66 4.03 3 9 3s9-1.34 9-3v-7"
                stroke={color}
                strokeWidth="1.5"
                fill="none"
            />
        </svg>
    );
};

function DatabaseListing() {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    // State
    const [loading, setLoading] = useState(true);
    const [viewMode, setViewMode] = useState(1); // 1=Card, 0=Table (List)
    const [deleteModalShow, setDeleteModalShow] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [deletePayload, setDeletePayload] = useState({ id: "", name: "", environment: "" });
    const [passwordModalShow, setPasswordModalShow] = useState(false);
    const [deletePassword, setDeletePassword] = useState("");
    const [stopModalShow, setStopModalShow] = useState(false);
    const [stopLoading, setStopLoading] = useState(false);
    const [stopPayload, setStopPayload] = useState({ id: "", name: "" });

    // Redux state
    const { databases: apiDatabases, isLoading, isActionLoading, actionDbId } = useSelector(
        (state) => state.databaseredux
    );
    const DatabaseGetListData = useSelector(
        (state) => state.databaseredux?.DatabaseGetListData
    );

    // Use real API data
    const databases = apiDatabases || [];

    // Check if any database is in transitional state (needs polling)
    const hasTransitionalDatabases = databases?.some(db =>
        ["starting", "restarting", "stopping", "creating", "pending"].includes(db.status)
    );

    // Fetch databases on mount
    useEffect(() => {
        fetchDatabases();

        return () => {
            dispatch(setDatabaseGetListData());
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Poll for status updates when databases are in transitional state
    useEffect(() => {
        let pollInterval;

        if (hasTransitionalDatabases && !loading) {
            pollInterval = setInterval(() => {
                dispatch(DatabaseGetList());
            }, 5000); // Poll every 5 seconds
        }

        return () => {
            if (pollInterval) {
                clearInterval(pollInterval);
            }
        };
    }, [hasTransitionalDatabases, loading, dispatch]);

    // Handle Redux response
    useEffect(() => {
        if (DatabaseGetListData !== null) {
            setLoading(false);
        }
    }, [DatabaseGetListData]);

    useEffect(() => {
        if (!isLoading && loading) {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoading]);

    const fetchDatabases = () => {
        setLoading(true);
        dispatch(DatabaseGetList());
    };

    // Calculate stats
    const stats = {
        total: databases?.length || 0,
        running: databases?.filter((db) => db.status === "running").length || 0,
        stopped: databases?.filter((db) => db.status === "stopped").length || 0,
        provisioning: databases?.filter((db) =>
            ["creating", "pending", "starting"].includes(db.status)
        ).length || 0,
    };

    // Check if a database is in transitional state
    const isDbTransitional = (status) =>
        ["starting", "restarting", "stopping", "creating", "pending"].includes(status);

    // Animated dots component for loading states - cycles through . .. ...
    const AnimatedDots = () => (
        <span className="animated-dots-cycle"></span>
    );

    // Get status display
    const getStatusBadge = (status) => {
        const statusConfig = {
            running: { class: "status-running", label: "Running" },
            stopped: { class: "status-stopped", label: "Stopped" },
            starting: { class: "status-provisioning", label: "Starting", animated: true },
            restarting: { class: "status-provisioning", label: "Restarting", animated: true },
            creating: { class: "status-provisioning", label: "Creating", animated: true },
            pending: { class: "status-provisioning", label: "Pending", animated: true },
            stopping: { class: "status-provisioning", label: "Stopping", animated: true },
            error: { class: "status-error", label: "Error" },
            failed: { class: "status-error", label: "Failed" },
        };
        const config = statusConfig[status] || { class: "status-unknown", label: status };

        return (
            <span className={`database-status-badge ${config.class}`}>
                <span className="status-dot"></span>
                {config.label}
                {config.animated && <AnimatedDots />}
            </span>
        );
    };

    // Format endpoint display
    const formatEndpoint = (db) => {
        if (db.endpoints?.production?.endpoint) {
            return db.endpoints.production.endpoint;
        }
        if (db.endpoint && !db.endpoint.startsWith("localhost")) {
            return db.endpoint;
        }
        if (db.status === "running" || db.status === "starting") {
            return "Provisioning...";
        }
        return "Pending...";
    };

    // Handle delete
    const handleDeleteClick = (db, e) => {
        e?.stopPropagation();
        setDeletePayload({ id: db.id, name: db.name, environment: db.environment });
        setDeleteModalShow(true);
    };

    const handleConfirmDelete = async () => {
        // If production, show password modal
        if (deletePayload.environment === "production") {
            setDeleteModalShow(false);
            setPasswordModalShow(true);
            return;
        }
        await performDelete();
    };

    const handlePasswordDelete = async () => {
        if (!deletePassword.trim()) {
            toast.error("Password is required for production databases");
            return;
        }
        await performDelete(deletePassword);
    };

    const performDelete = async (password) => {
        setDeleteLoading(true);
        const result = await dispatch(DatabaseDelete({ id: deletePayload.id, password }));
        setDeleteLoading(false);
        if (result?.payload?.isValidate) {
            toast.success("Database deleted successfully");
        } else {
            toast.error(result?.payload?.message || "Failed to delete database");
        }
        setDeleteModalShow(false);
        setPasswordModalShow(false);
        setDeletePassword("");
        setDeletePayload({ id: "", name: "", environment: "" });
        fetchDatabases();
    };

    // Handle start/stop
    const handleStartDatabase = async (id, e) => {
        e?.stopPropagation();
        await dispatch(DatabaseStart(id));
        setTimeout(() => fetchDatabases(), 1500);
    };

    const handleStopDatabase = (id, name, e) => {
        e?.stopPropagation();
        setStopPayload({ id, name });
        setStopModalShow(true);
    };

    const handleConfirmStop = async () => {
        setStopLoading(true);
        await dispatch(DatabaseStop(stopPayload.id));
        setStopLoading(false);
        setStopModalShow(false);
        setTimeout(() => fetchDatabases(), 1500);
    };

    // Navigate to detail
    const handleViewDatabase = (id) => {
        navigate(`/database/${id}`);
    };

    return (
        <div className="database-page-main schedular-page-main all-page-top">
            <Container>
                {/* Header Section */}
                <div className="top-bot-section flex-column align-items-start gap-3">
                    <div className="d-flex justify-content-between align-items-center w-100">
                        <h6>
                            <img
                                src={homeImage}
                                alt="home"
                                title="home"
                                style={{ cursor: "pointer", marginBottom: "3px" }}
                                onClick={() => navigate("/")}
                            />
                            <span style={{ fontWeight: "400", color: "#666666" }}>{" | "}</span>
                            <span className="mt-1">Database Instances</span>
                        </h6>

                        {/* Actions */}
                        <div className="d-flex gap-2 align-items-center">
                            {/* View Toggle */}
                            <div className="custom-tabing singledesh-tab">
                                <div
                                    className={viewMode === 1 ? "single-tab active" : "single-tab"}
                                    onClick={() => setViewMode(1)}
                                >
                                    <img src={cardicon} alt="cardicon" />
                                    <span>Card</span>
                                </div>
                                <div
                                    className={viewMode === 0 ? "single-tab active" : "single-tab"}
                                    onClick={() => setViewMode(0)}
                                >
                                    <img src={listicon} alt="listicon" />
                                    <span>List</span>
                                </div>
                            </div>

                            {/* Refresh Button */}
                            <div
                                className={`filtering-main ${isActionLoading ? "disabled" : ""}`}
                                onClick={!isActionLoading ? fetchDatabases : undefined}
                                style={{ cursor: isActionLoading ? "not-allowed" : "pointer", opacity: isActionLoading ? 0.5 : 1 }}
                            >
                                <svg
                                    width="14"
                                    height="14"
                                    viewBox="0 0 16 16"
                                    fill="currentColor"
                                    className={loading ? "spin-animation" : ""}
                                >
                                    <path d="M13.65 2.35A7.958 7.958 0 008 0C3.58 0 .01 3.58.01 8S3.58 16 8 16c3.73 0 6.84-2.55 7.73-6h-2.08A5.99 5.99 0 018 14 6 6 0 012 8a6 6 0 016-6c1.66 0 3.14.69 4.22 1.78L9 7h7V0l-2.35 2.35z" />
                                </svg>
                                <span>Refresh</span>
                            </div>

                            {/* Create Button */}
                            <div
                                className="filtering-main"
                                onClick={() => navigate("/database/create")}
                                style={{ cursor: "pointer" }}
                            >
                                <img src={addicon} alt="addicon" />
                                <span>Create Database</span>
                            </div>
                        </div>
                    </div>

                    {/* Stats Cards */}
                    {loading ? (
                        <StatsSkeleton />
                    ) : (
                        <div className="my-machine-top-boxes-wrapper">
                            <div className="my-machine-top-box-single total-machines in-process-machines scheduler-status-box">
                                <div>
                                    <h4>{stats.total}</h4>
                                </div>
                                <p>Total Databases</p>
                            </div>
                            <div className="my-machine-top-box-single pending-machines approved-machines scheduler-status-box">
                                <div>
                                    <h4>{stats.running}</h4>
                                </div>
                                <p>Running</p>
                            </div>
                            <div className="my-machine-top-box-single pending-machines rejected-machines scheduler-status-box">
                                <div>
                                    <h4>{stats.stopped}</h4>
                                </div>
                                <p>Stopped</p>
                            </div>
                            <div className="my-machine-top-box-single pending-machines scheduled-machines scheduler-status-box">
                                <div>
                                    <h4>{stats.provisioning}</h4>
                                </div>
                                <p>Provisioning</p>
                            </div>
                        </div>
                    )}
                </div>
            </Container>

            {/* Main Content */}
            <div className="main-page-content">
                <Container>
                    {/* Card View */}
                    {viewMode === 1 ? (
                        loading ? (
                            <DatabaseCardsGridSkeleton count={6} />
                        ) : databases?.length > 0 ? (
                            <div className="database-cards-grid">
                                {databases.map((db) => (
                                    <div
                                        key={db.id}
                                        className="database-card"
                                        onClick={() => handleViewDatabase(db.id)}
                                    >
                                        {/* Card Header */}
                                        <div className="database-card-header">
                                            <div className="database-card-title-row">
                                                <div className="database-card-icon">
                                                    <DatabaseIcon type={db.type} size={28} />
                                                </div>
                                                <div className="database-card-info">
                                                    <h5 className="database-card-name">{db.name}</h5>
                                                    <div className="database-card-meta">
                                                        <span className="database-engine-badge">
                                                            {(db.type || "postgresql").toUpperCase()}
                                                        </span>
                                                        <span className="database-version">
                                                            v{db.version || "16"}
                                                        </span>
                                                        {getStatusBadge(db.status)}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Card Body */}
                                        <div className="database-card-body">
                                            <div className="database-card-row">
                                                <span className="database-card-label">ENDPOINT</span>
                                                <span className="database-card-value">
                                                    <code>{formatEndpoint(db)}</code>
                                                </span>
                                            </div>
                                            <div className="database-card-row">
                                                <span className="database-card-label">DATABASE</span>
                                                <span className="database-card-value">
                                                    {db.database || "appdb"}
                                                </span>
                                            </div>
                                            <div className="database-card-row">
                                                <span className="database-card-label">STORAGE</span>
                                                <span className="database-card-value">
                                                    {db.storage || "20Gi"}
                                                </span>
                                            </div>
                                            <div className="database-card-row">
                                                <span className="database-card-label">CONNECTIONS</span>
                                                <span className="database-card-value">
                                                    Max: {db.maxConnections || 100}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Card Footer */}
                                        <div className="database-card-footer">
                                            <span className="database-created">
                                                Created{" "}
                                                {db.createdAt
                                                    ? moment(db.createdAt).format("MMM DD, YYYY")
                                                    : "-"}
                                            </span>
                                            <div
                                                className="database-card-actions"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                {/* Start/Stop */}
                                                {db.status === "running" ? (
                                                    <button
                                                        className="db-action-btn stop-btn"
                                                        title="Stop Database"
                                                        onClick={(e) => handleStopDatabase(db.id, db.name, e)}
                                                        disabled={isActionLoading && actionDbId === db.id}
                                                    >
                                                        {isActionLoading && actionDbId === db.id ? (
                                                            <Spinner animation="border" size="sm" />
                                                        ) : (
                                                            <svg
                                                                width="14"
                                                                height="14"
                                                                viewBox="0 0 16 16"
                                                                fill="currentColor"
                                                            >
                                                                <rect x="3" y="3" width="10" height="10" rx="1" />
                                                            </svg>
                                                        )}
                                                    </button>
                                                ) : db.status === "stopped" ? (
                                                    <button
                                                        className="db-action-btn start-btn"
                                                        title="Start Database"
                                                        onClick={(e) => handleStartDatabase(db.id, e)}
                                                        disabled={isActionLoading && actionDbId === db.id}
                                                    >
                                                        {isActionLoading && actionDbId === db.id ? (
                                                            <Spinner animation="border" size="sm" />
                                                        ) : (
                                                            <svg
                                                                width="14"
                                                                height="14"
                                                                viewBox="0 0 16 16"
                                                                fill="currentColor"
                                                            >
                                                                <path d="M4 2l10 6-10 6V2z" />
                                                            </svg>
                                                        )}
                                                    </button>
                                                ) : (
                                                    <button className="db-action-btn" disabled>
                                                        <Spinner animation="border" size="sm" />
                                                    </button>
                                                )}

                                                {/* View */}
                                                <button
                                                    className="db-action-btn view-btn"
                                                    title="View Details"
                                                    onClick={() => handleViewDatabase(db.id)}
                                                >
                                                    <svg
                                                        width="14"
                                                        height="14"
                                                        viewBox="0 0 16 16"
                                                        fill="currentColor"
                                                    >
                                                        <path d="M8 3C3 3 0 8 0 8s3 5 8 5 8-5 8-5-3-5-8-5zm0 8a3 3 0 110-6 3 3 0 010 6z" />
                                                    </svg>
                                                </button>

                                                {/* Delete */}
                                                <button
                                                    className="db-action-btn delete-btn"
                                                    title="Delete Database"
                                                    onClick={(e) => handleDeleteClick(db, e)}
                                                    disabled={isDbTransitional(db.status)}
                                                >
                                                    <svg
                                                        width="14"
                                                        height="14"
                                                        viewBox="0 0 16 16"
                                                        fill="currentColor"
                                                    >
                                                        <path d="M5.5 0v1H1v2h14V1h-4.5V0h-5zM2 4v11a1 1 0 001 1h10a1 1 0 001-1V4H2z" />
                                                    </svg>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))
                                }
                            </div>
                        ) : (
                            <div className="scheduler-table-main database-table-main" style={{ width: "100%" }}>
                                <div className="table-wrapper-main" style={{ minHeight: "300px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                    <Nodata />
                                </div>
                            </div>
                        )
                    ) : (
                        /* Table/List View */
                        databases?.length > 0 || loading ? (
                            <div className="scheduler-table-main database-table-main">
                                <div className="table-wrapper-main">
                                    <Table responsive>
                                        <thead>
                                            <tr>
                                                <th className="width200 sticky-workflow text-nowrap">
                                                    Database Name
                                                </th>
                                                <th className="text-center width100">Engine</th>
                                                <th className="text-center width100">Version</th>
                                                <th className="text-center width120">Status</th>
                                                <th className="width250">Endpoint</th>
                                                <th className="text-center width100">Storage</th>
                                                <th className="text-center width120">Connections</th>
                                                <th className="text-center width150">Created On</th>
                                                <th className="text-end width150 sticky-actions">
                                                    Actions
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {loading ? (
                                                <Loader type={LoaderType.TableRow} count={5} nooftd={9} />
                                            ) : (
                                                <>
                                                    {databases?.length > 0 ? (
                                                        databases.map((db) => (
                                                            <tr
                                                                key={db.id}
                                                                onClick={() => handleViewDatabase(db.id)}
                                                                style={{ cursor: "pointer" }}
                                                            >
                                                                <td className="theme-color-text fw600 sticky-workflow text-nowrap">
                                                                    <div className="d-flex align-items-center gap-2">
                                                                        <DatabaseIcon type={db.type} size={22} />
                                                                        <span>{db.name}</span>
                                                                    </div>
                                                                </td>
                                                                <td className="text-center text-uppercase">
                                                                    {db.type || "postgresql"}
                                                                </td>
                                                                <td className="text-center">
                                                                    v{db.version || "16"}
                                                                </td>
                                                                <td className="text-center">
                                                                    {getStatusBadge(db.status)}
                                                                </td>
                                                                <td>
                                                                    <code
                                                                        className="endpoint-code"
                                                                        style={{
                                                                            fontSize: "12px",
                                                                            background: "#f5f5f5",
                                                                            padding: "2px 6px",
                                                                            borderRadius: "4px",
                                                                        }}
                                                                    >
                                                                        {formatEndpoint(db)}
                                                                    </code>
                                                                </td>
                                                                <td className="text-center">
                                                                    {db.storage || "20Gi"}
                                                                </td>
                                                                <td className="text-center">
                                                                    Max: {db.maxConnections || 100}
                                                                </td>
                                                                <td className="text-center">
                                                                    {db.createdAt
                                                                        ? moment(db.createdAt).format("MMM DD, YYYY")
                                                                        : "-"}
                                                                </td>
                                                                <td
                                                                    className="sticky-actions"
                                                                    onClick={(e) => e.stopPropagation()}
                                                                >
                                                                    <div className="all-actions Scheduler-page">
                                                                        {/* Start/Stop Button */}
                                                                        {db.status === "running" ? (
                                                                            <span
                                                                                onClick={(e) => handleStopDatabase(db.id, db.name, e)}
                                                                                title="Stop Database"
                                                                                style={{ cursor: "pointer" }}
                                                                            >
                                                                                {isActionLoading && actionDbId === db.id ? (
                                                                                    <Spinner
                                                                                        animation="border"
                                                                                        size="sm"
                                                                                        style={{ width: "14px", height: "14px" }}
                                                                                    />
                                                                                ) : (
                                                                                    <svg
                                                                                        width="16"
                                                                                        height="16"
                                                                                        viewBox="0 0 16 16"
                                                                                        fill="#dc3545"
                                                                                    >
                                                                                        <rect
                                                                                            x="3"
                                                                                            y="3"
                                                                                            width="10"
                                                                                            height="10"
                                                                                            rx="1"
                                                                                        />
                                                                                    </svg>
                                                                                )}
                                                                            </span>
                                                                        ) : db.status === "stopped" ? (
                                                                            <span
                                                                                onClick={(e) => handleStartDatabase(db.id, e)}
                                                                                title="Start Database"
                                                                                style={{ cursor: "pointer" }}
                                                                            >
                                                                                {isActionLoading && actionDbId === db.id ? (
                                                                                    <Spinner
                                                                                        animation="border"
                                                                                        size="sm"
                                                                                        style={{ width: "14px", height: "14px" }}
                                                                                    />
                                                                                ) : (
                                                                                    <svg
                                                                                        width="16"
                                                                                        height="16"
                                                                                        viewBox="0 0 16 16"
                                                                                        fill="#28a745"
                                                                                    >
                                                                                        <path d="M4 2l10 6-10 6V2z" />
                                                                                    </svg>
                                                                                )}
                                                                            </span>
                                                                        ) : (
                                                                            <span>
                                                                                <Spinner
                                                                                    animation="border"
                                                                                    size="sm"
                                                                                    style={{ width: "14px", height: "14px" }}
                                                                                />
                                                                            </span>
                                                                        )}

                                                                        {/* View Button */}
                                                                        <img
                                                                            src={viewAction}
                                                                            alt="viewAction"
                                                                            title="View Details"
                                                                            onClick={() => handleViewDatabase(db.id)}
                                                                            style={{ cursor: "pointer" }}
                                                                        />

                                                                        {/* Delete Button */}
                                                                        <img
                                                                            src={deleteAction}
                                                                            alt="deleteAction"
                                                                            title="Delete Database"
                                                                            onClick={(e) => !isDbTransitional(db.status) && handleDeleteClick(db, e)}
                                                                            style={{
                                                                                cursor: isDbTransitional(db.status) ? "not-allowed" : "pointer",
                                                                                opacity: isDbTransitional(db.status) ? 0.4 : 1
                                                                            }}
                                                                        />
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        ))
                                                    ) : (
                                                        <tr>
                                                            <td colSpan={9}>
                                                                <Nodata />
                                                            </td>
                                                        </tr>
                                                    )}
                                                </>
                                            )}
                                        </tbody>
                                    </Table>
                                </div>
                            </div>
                        ) : (
                            <div className="scheduler-table-main database-table-main" style={{ width: "100%" }}>
                                <div className="table-wrapper-main" style={{ minHeight: "300px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                    <Nodata />
                                </div>
                            </div>
                        )
                    )}
                </Container>
            </div>

            {/* Delete Confirmation Modal */}
            <Modal
                show={deleteModalShow}
                onHide={() => setDeleteModalShow(false)}
                size="md"
                centered
                className="delete-modal"
                backdrop="static"
            >
                <Modal.Body>
                    <div className="text-center">
                        <img src={bin} alt="bin" className="modal-delete-icon" />
                        <h4>Delete Database?</h4>
                        <p>
                            Are you sure you want to delete <strong>"{deletePayload.name}"</strong>?
                            <br />This action cannot be undone and all data will be permanently lost.
                        </p>
                        <div className="d-flex align-items-center gap-2 justify-content-center">
                            <button
                                className="grey-btn"
                                onClick={() => setDeleteModalShow(false)}
                                disabled={deleteLoading}
                            >
                                No, Keep
                            </button>
                            <button
                                className="red-btn"
                                onClick={handleConfirmDelete}
                                disabled={deleteLoading}
                            >
                                {deleteLoading ? (
                                    <Spinner animation="border" size="sm" />
                                ) : (
                                    "Yes, Delete"
                                )}
                            </button>
                        </div>
                    </div>
                </Modal.Body>
            </Modal>

            {/* Password Confirmation Modal for Production Databases */}
            <Modal
                show={passwordModalShow}
                onHide={() => { setPasswordModalShow(false); setDeletePassword(""); }}
                size="md"
                centered
                className="delete-modal"
                backdrop="static"
            >
                <Modal.Body>
                    <div className="text-center">
                        <img src={bin} alt="bin" className="modal-delete-icon" />
                        <h4>Production Database</h4>
                        <p>
                            Please enter your password to confirm deletion of <strong>"{deletePayload.name}"</strong>.
                        </p>
                        <div className="mb-3">
                            <input
                                type="password"
                                className="form-control"
                                placeholder="Enter your password"
                                value={deletePassword}
                                onChange={(e) => setDeletePassword(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && handlePasswordDelete()}
                                autoFocus
                            />
                        </div>
                        <div className="d-flex align-items-center gap-2 justify-content-center">
                            <button
                                className="grey-btn"
                                onClick={() => { setPasswordModalShow(false); setDeletePassword(""); }}
                                disabled={deleteLoading}
                            >
                                Cancel
                            </button>
                            <button
                                className="red-btn"
                                onClick={handlePasswordDelete}
                                disabled={deleteLoading || !deletePassword.trim()}
                            >
                                {deleteLoading ? (
                                    <Spinner animation="border" size="sm" />
                                ) : (
                                    "Delete"
                                )}
                            </button>
                        </div>
                    </div>
                </Modal.Body>
            </Modal>

            {/* Stop Confirmation Modal */}
            <Modal
                show={stopModalShow}
                onHide={() => setStopModalShow(false)}
                size="md"
                centered
                className="delete-modal stop-modal"
                backdrop="static"
            >
                <Modal.Body>
                    <div className="text-center">
                        <div className="modal-stop-icon">
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#e8586d" strokeWidth="2">
                                <circle cx="12" cy="12" r="10" />
                                <line x1="12" y1="8" x2="12" y2="12" />
                                <line x1="12" y1="16" x2="12.01" y2="16" />
                            </svg>
                        </div>
                        <h4>Stop Database?</h4>
                        <p>
                            Are you sure you want to stop <strong>"{stopPayload.name}"</strong>?
                            <br />Stopping the database will disconnect all active connections.
                        </p>
                        <div className="d-flex align-items-center gap-2 justify-content-center">
                            <button
                                className="grey-btn"
                                onClick={() => setStopModalShow(false)}
                                disabled={stopLoading}
                            >
                                Cancel
                            </button>
                            <button
                                className="red-btn stop-btn"
                                onClick={handleConfirmStop}
                                disabled={stopLoading}
                            >
                                {stopLoading ? (
                                    <Spinner animation="border" size="sm" />
                                ) : (
                                    "Yes, Stop"
                                )}
                            </button>
                        </div>
                    </div>
                </Modal.Body>
            </Modal>
        </div>
    );
}

export default DatabaseListing;

